import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Monitor,
  Type,
  Image,
  Link as LinkIcon,
  Youtube,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  X,
} from "lucide-react";

const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const AdminBanner = () => {
  const [data, setData] = useState({
    badgeText: "",
    mainTitle: "",
    description: "",
    primaryButton: { text: "", link: "" },
    secondaryButton: { text: "", link: "" },
    youtubeUrl: "",
    backgroundShape: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/banner-section`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleButtonChange = (type, field, value) => {
    setData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/banner-section`, data);
      showMessage("Banner updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update banner", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset to default settings?"))
      return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/banner-section/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Banner reset to default", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/banner-section/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(
        `Banner ${newStatus ? "activated" : "deactivated"}`,
        "success",
      );
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading)
    return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Banner Section Management</h3>
        <div className="d-flex gap-2">
          <button
            className={`btn btn-sm ${data.isActive ? "btn-success" : "btn-outline-secondary"} d-flex align-items-center gap-2`}
            onClick={toggleStatus}
          >
            {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
            {data.isActive ? "Active" : "Disabled"}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
            onClick={handleReset}
            disabled={saving}
          >
            <RefreshCw size={18} /> Reset
          </button>
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-2"
            onClick={handleSubmit}
            disabled={saving}
          >
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`alert alert-${message.type === "error" ? "danger" : message.type} mb-4`}
        >
          {message.text}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setMessage(null)}
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Content Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                <Type size={20} /> Content
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold">Badge Text</label>
                  <input
                    type="text"
                    name="badgeText"
                    className="form-control"
                    value={data.badgeText}
                    onChange={handleChange}
                    placeholder="e.g. Next-Gen AI Content Suite"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">
                    Main Title (Use &lt;br /&gt; for line breaks)
                  </label>
                  <textarea
                    name="mainTitle"
                    className="form-control"
                    rows="2"
                    value={data.mainTitle}
                    onChange={handleChange}
                    placeholder="Enter main title..."
                  />
                </div>
                <div className="mb-0">
                  <label className="form-label small fw-bold">
                    Description
                  </label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows="3"
                    value={data.description}
                    onChange={handleChange}
                    placeholder="Enter description..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Buttons Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                <LinkIcon size={20} /> Buttons
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label small fw-bold">
                      Primary Button Text
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={data.primaryButton?.text || ""}
                      onChange={(e) =>
                        handleButtonChange(
                          "primaryButton",
                          "text",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label small fw-bold">
                      Primary Button Link
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={data.primaryButton?.link || ""}
                      onChange={(e) =>
                        handleButtonChange(
                          "primaryButton",
                          "link",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
                <div className="row mb-0">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label className="form-label small fw-bold">
                      Secondary Button Text
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={data.secondaryButton?.text || ""}
                      onChange={(e) =>
                        handleButtonChange(
                          "secondaryButton",
                          "text",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">
                      Secondary Button Link
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={data.secondaryButton?.link || ""}
                      onChange={(e) =>
                        handleButtonChange(
                          "secondaryButton",
                          "link",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Media Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                <Image size={20} /> Media
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold">
                    <Youtube size={14} className="me-1" /> YouTube URL
                  </label>
                  <input
                    type="text"
                    name="youtubeUrl"
                    className="form-control"
                    value={data.youtubeUrl}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminBanner;
